import React from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const basicsSteps = [
  {
    label: 'Part1',
    title: 'Kiroとは: AIとの対話の基本原則',
    path: '/devdocs/workflow/kiro-basics-1',
    active: true,
  },
  {
    label: 'Part2',
    title: 'Steeringファイル: AIにルールを伝える方法',
    path: '/devdocs/workflow/kiro-basics-2',
  },
  {
    label: 'Part3',
    title: '機能と運用: 知っておくべきKiroの機能',
    path: '/devdocs/workflow/kiro-basics-3',
  },
  {
    label: 'Part4',
    title: 'ワークフロー全体像: 本プロジェクトの進め方',
    path: '/devdocs/workflow/kiro-basics-4',
  },
];
const WorkflowKiroBasics1: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="Kiro基本編 Part1 — Kiroとは: AIとの対話の基本原則"
      seriesName="Kiro基本編"
      tags={['Kiro', 'Context', 'Credit']}
      seriesSteps={basicsSteps}
      nextLink={{
        label: 'Part2: Steeringファイル',
        path: '/devdocs/workflow/kiro-basics-2',
      }}
    >
      {/* この記事で学べること */}
      <WorkflowLearningPoints
        items={[
          'Kiroとは何か、どんな開発ツールか',
          'コンテキスト（AIの参照情報）の仕組みと管理方法',
          '# コンテキスト指定の効果的な使い方',
          'クレジット（トークン消費）の節約テクニック',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: 'Kiroとは' },
          { id: 'section-2', label: 'コンテキストを理解する' },
          { id: 'section-3', label: '# コンテキスト指定の効果的な使い方' },
          { id: 'section-4', label: 'クレジット（トークン消費）の節約テクニック' },
          { id: 'section-5', label: 'ベストプラクティスとアンチパターン' },
        ]}
      />

      {/* 1. Kiroとは */}
      <WorkflowSection title="Kiroとは" id="section-1" number={1}>
        <WorkflowText>
          KiroはAI駆動の統合開発環境（IDE）です。VS
          Codeをベースに構築されており、生成AIがコードの設計・実装・テストまでを支援します。
        </WorkflowText>
        <WorkflowText>
          従来のAIコーディングアシスタント（GitHub
          Copilotなど）とは異なり、Kiroは「設計書からコード生成までの一連のワークフロー」を構造的に管理できる点が特徴です。
        </WorkflowText>

        <Grid container spacing={1.5} sx={{ mt: 1 }}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FeatureCard
              title="Steering（操舵）"
              description="ルールファイルでAIの振る舞いを制御する"
              color="#1565c0"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FeatureCard
              title="Specs（仕様）"
              description="要件→設計→タスクの段階的な仕様策定"
              color="#2e7d32"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <FeatureCard
              title="Hooks（自動化）"
              description="ファイル保存やコミット時にAIが自動実行"
              color="#e65100"
            />
          </Grid>
        </Grid>
      </WorkflowSection>

      {/* 2. コンテキストを理解する */}
      <WorkflowSection title="コンテキストを理解する" id="section-2" number={2}>
        <Box
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            bgcolor: (theme) =>
              theme.palette.mode === 'dark'
                ? 'rgba(25, 118, 210, 0.06)'
                : 'rgba(25, 118, 210, 0.04)',
            mb: 1.5,
          }}
        >
          <Chip
            label="基本概念"
            size="small"
            color="primary"
            sx={{ height: 20, fontSize: '0.65rem', mb: 1 }}
          />
          <WorkflowText>
            AI駆動開発で最も重要な概念が <strong>コンテキスト（context）</strong>{' '}
            です。コンテキストとは、AIが1回の応答を生成するために参照できる情報の全体のことです。AIはコンテキストに含まれる情報だけを頼りに判断します。逆に言えば、コンテキストに含まれていない情報は「存在しない」のと同じです。
          </WorkflowText>
        </Box>

        <WorkflowSection title="コンテキストに含まれるもの" subNumber="2.1">
          <WorkflowTable
            headers={['種類', '具体例']}
            rows={[
              ['システムプロンプト', 'Steeringファイルの内容、Kiroの内部ルール'],
              ['チャット履歴', 'ユーザーとAIのやりとりの蓄積'],
              ['ファイル内容', '#ファイル名 で渡したファイル、AIが読み込んだファイル'],
              ['ツール実行結果', 'コマンド実行結果、検索結果など'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="コンテキスト枠（トークン上限）" subNumber="2.2">
          <WorkflowText>
            AIが一度に参照できる情報量には上限があります（トークン上限）。この上限をコンテキスト枠と呼びます。
          </WorkflowText>
          <Box
            sx={{
              border: 2,
              borderColor: 'primary.main',
              borderRadius: 2,
              p: 2,
              bgcolor: (theme) =>
                theme.palette.mode === 'dark'
                  ? 'rgba(25, 118, 210, 0.06)'
                  : 'rgba(25, 118, 210, 0.04)',
            }}
          >
            <Chip
              label="コンテキスト枠（上限あり）"
              size="small"
              color="primary"
              sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, mb: 1.5 }}
            />
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ justifyContent: 'center', flexWrap: 'wrap', gap: 1 }}
            >
              {['Steering', 'チャット履歴', 'ファイル', 'ツール実行結果'].map((label) => (
                <Box
                  key={label}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    px: 2,
                    py: 1,
                    bgcolor: 'background.paper',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" fontWeight={600}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </WorkflowSection>

        <WorkflowSection title="コンテキスト枠を超えるとどうなるか" subNumber="2.3">
          <WorkflowText>
            対話が長くなったり、大きなファイルを複数読み込んだりすると、コンテキスト枠の上限に達します。上限に達すると、Kiroはコンパクション（自動要約圧縮）を行い、古いチャット履歴を要約して枠を確保します。
          </WorkflowText>
          <WorkflowList
            items={[
              '対話の前半で決めた細かい仕様（カラム名、制約値など）が要約で落ちる',
              'AIが「決めたはず」の内容を参照できなくなり、矛盾した提案をする',
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="気をつけるべきこと" subNumber="2.4">
          <WorkflowInfoBox type="important">
            <WorkflowTable
              headers={['原則', '説明']}
              rows={[
                [
                  '重要な決定はファイルに書き出す',
                  'ファイルに書いた情報はコンパクションの影響を受けない。いつでも #ファイル名 で正確に参照できる',
                ],
                [
                  'コンテキストに渡す情報を絞る',
                  '関係ないファイルを大量に渡すと枠を圧迫する。必要なファイルだけを # で指定する',
                ],
                [
                  'Steeringのalwaysは最小限に',
                  'always指定のSteeringは毎回コンテキストを消費する。本当に常時必要なものだけにする',
                ],
                [
                  '長い対話は分割する',
                  '1セッションで全てを決めようとせず、途中経過をファイルに書き出して次のセッションに引き継ぐ',
                ],
              ]}
            />
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection
          title="コンテキストがいっぱいになったら — セッションの切り替え手順"
          subNumber="2.5"
        >
          <WorkflowText>
            対話が長時間になり、AIが以前の決定事項を忘れたり同じ質問を繰り返し始めたら、コンテキスト枠の限界に達したサインです。新しいセッションに切り替えましょう。
          </WorkflowText>

          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
            Step 1. 現在の進捗をファイルに書き出す
          </Typography>
          <WorkflowChatBubble role="human">
            ここまでの決定事項を&#123;作業中のファイル&#125;.mdの決定事項セクションに追記して。
            {'\n'}
            まだ未決定の事項があれば「## 未決定事項」セクションに箇条書きで残して。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            Step 2. 次のセッションでやるべきことを明記する
          </Typography>
          <WorkflowChatBubble role="human">
            次のセッションで続きをやるために、{'\n'}
            「## 次回のTODO」セクションに残タスクを書き出して。
          </WorkflowChatBubble>

          <Typography variant="body2" fontWeight={600} sx={{ mt: 1.5, mb: 0.5 }}>
            Step 3. 新しいセッションを開始する
          </Typography>
          <WorkflowText sx={{ mb: 0.5 }}>
            新しいセッションで、書き出したファイルをコンテキストに含めて作業を再開します。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            #&#123;作業中のファイル&#125;.md の続きをやりたい。{'\n'}
            「次回のTODO」セクションの内容から再開して。
          </WorkflowChatBubble>

          <WorkflowInfoBox>
            セッション切り替えのコツは「次のセッションの自分（とAI）が、前回の対話を読み返さなくても作業を再開できる状態」をファイルに残すことです。AIは前のセッションのチャット履歴を一切参照できないため、ファイルに書かれていない情報は完全に失われます。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 3. # コンテキスト指定 */}
      <WorkflowSection title="# コンテキスト指定の効果的な使い方" id="section-3" number={3}>
        <WorkflowText>
          チャットで # を使うと、特定のファイルや情報をAIのコンテキストに明示的に渡せます。
        </WorkflowText>

        <WorkflowSection title="利用可能な # 指定" subNumber="3.1">
          <WorkflowTable
            headers={['指定', '内容', 'コンテキスト消費']}
            rows={[
              ['#ファイル名', '指定ファイルの内容を渡す', 'ファイルサイズに比例（大）'],
              ['#フォルダ名', 'フォルダ内のファイル一覧を渡す', 'ファイル数に比例（中）'],
              ['#Problems', '現在のファイルの問題（エラー・警告）', '小'],
              ['#Terminal', 'ターミナルの最新出力', '出力量に比例（中）'],
              ['#Git Diff', '現在のGit差分', '差分量に比例（中〜大）'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="いつ何を渡すべきか" subNumber="3.2">
          <WorkflowTable
            headers={['シーン', '渡すべきもの', '渡さない方がよいもの']}
            rows={[
              ['バグ修正', 'エラーが出ているファイル + #Problems + #Terminal', '関係ないファイル'],
              ['API仕様書からDD書作成', '対象API仕様書 + 関連スキーマ定義', '他のAPIの仕様書'],
              ['コードレビュー', '#Git Diff', '変更していないファイル'],
              ['ワークフロー起動', '#api_project_rule.md + 対象API仕様書', '全DD書を一括指定'],
              ['テスト実行', '#local_test.md + #test.md', 'ソースコード全体'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="コンテキスト消費を抑えるテクニック" subNumber="3.3">
          <WorkflowTable
            headers={['テクニック', '効果']}
            rows={[
              ['必要なファイルだけを # で渡す', '不要な情報でコンテキスト枠を消費しない'],
              [
                'フォルダ指定は浅い階層に限定する',
                '深いフォルダ全体を渡すと大量のファイル情報がコンテキストに入る',
              ],
              [
                'AIに「読んで」と指示する',
                '# で渡す代わりに「〇〇ファイルを読んで確認して」と指示すると、AIが必要な部分だけ読む',
              ],
              [
                'Steering の fileMatch を活用する',
                '手動で毎回 # を付けずとも、対象ファイル編集時に自動で必要なルールが読み込まれる',
              ],
            ]}
          />
          <WorkflowInfoBox type="warning">
            #
            でファイルを渡すとファイルの全内容がコンテキストに入ります。1000行を超えるファイルを複数渡すとコンテキスト枠を大きく圧迫します。大きなファイルは「〇〇ファイルの△△メソッドを確認して」のように部分指定で読ませる方が効率的です。
          </WorkflowInfoBox>
        </WorkflowSection>
      </WorkflowSection>

      {/* 4. クレジット節約テクニック */}
      <WorkflowSection title="クレジット（トークン消費）の節約テクニック" id="section-4" number={4}>
        <WorkflowText>
          AIとのやりとりにはトークン（≒文字量）に基づくクレジットが消費されます。効率的にクレジットを使うことで、同じ予算でより多くの作業を完了できます。
        </WorkflowText>

        <WorkflowSection title="クレジットが消費される主な場面" subNumber="4.1">
          <WorkflowTable
            headers={['場面', '消費量', '説明']}
            rows={[
              [
                'Steeringファイルの読み込み',
                'always指定分は毎回消費',
                'always が多いほど毎回の基礎消費が増える',
              ],
              [
                'ファイルの読み込み',
                'ファイルサイズに比例',
                '大きなファイルを読むほど入力トークンが増える',
              ],
              ['コード生成', '出力量に比例', '大量のコードを一度に生成すると出力トークンが増える'],
              [
                'サブエージェントの利用',
                '独立したコンテキストで追加消費',
                'サブエージェントは自身のコンテキスト分を別途消費する',
              ],
              ['長い対話', 'チャット履歴が蓄積', '対話が長いほど毎回のコンテキストサイズが増える'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="本プロジェクトで実践した節約テクニック" subNumber="4.2">
          <WorkflowTable
            headers={['#', 'テクニック', '効果', '実装場所']}
            rows={[
              [
                '1',
                'サブエージェントを使わない',
                '独立コンテキストの追加消費を回避',
                'api_project_rule 共通ルール',
              ],
              [
                '2',
                '既存コードは同パターン1つだけ確認',
                '同種ファイルの重複読み込みを防止',
                'api_project_rule クレジット節約ルール',
              ],
              [
                '3',
                'DD書テンプレートの参照を省略',
                '過去に生成実績があれば記憶で十分',
                'api_project_rule クレジット節約ルール',
              ],
              [
                '4',
                'Rv指摘は一覧のみ読み込み',
                '詳細ファイルは必要なIDのみ参照',
                'api_project_rule クレジット節約ルール',
              ],
              [
                '5',
                'Rv指摘の追記は末尾追記（insert）',
                '全文読み込み→書き換えではなく差分追記',
                'api_project_rule クレジット節約ルール',
              ],
              [
                '6',
                'mk_instructions でフルコードを書かない',
                '出力トークンを抑制。方針のみ記載',
                'api_project_rule Phase2ルール',
              ],
              [
                '7',
                'local_test.md を manual にする',
                'テスト実行時以外のコンテキスト消費を防止',
                'Steering設定',
              ],
              [
                '8',
                'セッションを適切に切り替える',
                '長い対話の履歴蓄積を防止',
                'コンテキスト管理の基本原則',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="やりがちな無駄遣いパターン" subNumber="4.3">
          <WorkflowTable
            headers={['パターン', '何が起きるか', '改善策']}
            rows={[
              [
                'always のSteeringファイルが10個以上',
                '毎回の基礎消費が膨大になる',
                '本当に常時必要なもの（3〜5ファイル）に絞る',
              ],
              [
                '「念のため」で関連ファイルを全部 # で渡す',
                'コンテキスト枠の大半がファイル内容で埋まる',
                '必要最小限のファイルだけ渡す',
              ],
              [
                '1セッションでAPIを連続生成する',
                '対話履歴が膨大になりコンパクションが頻発',
                '1APIごとにセッションを切り替える',
              ],
              [
                'テスト失敗を何度もリトライさせる',
                '毎回コンテキスト全体を再読み込みして消費',
                '2回失敗したら根本原因を人間が判断する',
              ],
              [
                'フォルダ全体を # で渡す',
                'フォルダ内の全ファイル情報がコンテキストに入る',
                '必要なファイルを個別に指定する',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowTipBox title="クレジット消費の見積もり感覚">
          <WorkflowTable
            headers={['作業', '消費感覚']}
            rows={[
              ['Steering 3ファイル（product/structure/tech）', '基礎消費として毎回発生'],
              ['DD書1ファイルの読み込み', '小〜中'],
              ['Javaソースコード1クラスの生成', '中'],
              ['test.md（20ケース）の生成', '大'],
              ['サブエージェント1回呼び出し', '中〜大（独立コンテキスト分）'],
              ['コンパクション発生後の再読み込み', '中（圧縮前の情報を再度渡す必要がある）'],
            ]}
          />
          <WorkflowInfoBox>
            クレジット節約は「品質を犠牲にしてでも消費を減らす」ことではありません。不要な読み込みを減らし、同じ品質をより少ないトークンで達成することが目的です。品質に必要な情報（スキーマ定義、DD書、API仕様書）の読み込みは省略してはいけません。
          </WorkflowInfoBox>
        </WorkflowTipBox>
      </WorkflowSection>

      {/* 5. ベストプラクティスとアンチパターン */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-5" number={5}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: '重要な決定事項はチャットではなくファイルに書き出す',
              effect: 'ファイルはコンパクションの影響を受けず、いつでも正確に参照できる',
            },
            {
              practice: '# で渡すファイルは必要最小限にする',
              effect: 'コンテキスト枠を節約し、AIの応答精度を維持する',
            },
            {
              practice: 'セッションが長くなったらファイルに書き出して切り替える',
              effect:
                'コンパクションによる情報消失を防ぎ、常にフレッシュなコンテキストで作業できる',
            },
            {
              practice: '大きなファイルは部分指定で読ませる',
              effect: '# で全文を渡すのではなく「○○メソッドを確認して」と指示する',
            },
            {
              practice: 'AIに「読んで」と指示してファイルを自分で取得させる',
              effect: 'AIが必要な部分だけ読み込むためコンテキスト消費が最小になる',
            },
            {
              practice: 'クレジット節約ルールをSteeringに明記する',
              effect: 'AIが自律的に節約行動を取れる。人間が毎回指示する必要がなくなる',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '「念のため」で関連ファイルを全部 # で渡す',
              effect: 'コンテキスト枠の大半がファイル内容で埋まり、本来の作業に使える余地が減る',
            },
            {
              practice: '1セッションで大量のAPIを連続生成する',
              effect: '対話履歴が膨大になりコンパクションが頻発し、前半の決定事項が失われる',
            },
            {
              practice: '決定事項をチャット内だけに留める',
              effect: 'コンパクション時に消失し、AIが矛盾した提案をする',
            },
            {
              practice: 'テスト失敗を何度もリトライさせる',
              effect: '毎回コンテキスト全体を再読み込みして消費が膨大になる',
            },
            {
              practice: 'フォルダ全体を # で渡す',
              effect: 'フォルダ内の全ファイル情報がコンテキストに入り枠を圧迫する',
            },
            {
              practice: 'コンテキスト枠の限界サインを無視して対話を続ける',
              effect: 'AIの応答精度が低下し、同じ質問を繰り返す',
            },
          ]}
        />
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};
// --- Sub components ---

const FeatureCard: React.FC<{ title: string; description: string; color: string }> = ({
  title,
  description,
  color,
}) => (
  <Box
    sx={{
      border: 1,
      borderColor: 'divider',
      borderRadius: 2,
      p: 2,
      height: '100%',
      borderTop: 3,
      borderTopColor: color,
    }}
  >
    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
      {title}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {description}
    </Typography>
  </Box>
);

export default WorkflowKiroBasics1;
